/* ALANDRA — Cosmos theme scripts (vanilla, no dependencies) */
(() => {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const A = window.Alandra || {};

  /* ---------- Money ---------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = format || A.moneyFormat || '{{amount_with_comma_separator}} €';
    const value = Number(cents) / 100;
    const withDelims = (n, decimals, thousands, decimal) => {
      const parts = n.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount': return withDelims(value, 2, ',', '.');
        case 'amount_no_decimals': return withDelims(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelims(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(value, 0, '.', ',');
        case 'amount_with_space_separator': return withDelims(value, 2, ' ', ',');
        case 'amount_with_apostrophe_separator': return withDelims(value, 2, "'", '.');
        default: return withDelims(value, 2, '.', ',');
      }
    });
  }
  A.formatMoney = formatMoney;

  /* ---------- Header on scroll ---------- */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    let ticking = false;
    const update = () => { header.classList.toggle('is-scrolled', window.scrollY > 24); ticking = false; };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    update();
  }

  /* ---------- Mobile menu ---------- */
  function initMenu() {
    const drawer = $('[data-menu-drawer]');
    if (!drawer) return;
    const openers = $$('[data-menu-open]');
    const close = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      openers.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      document.body.classList.remove('is-locked');
    };
    const open = () => {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      openers.forEach((b) => b.setAttribute('aria-expanded', 'true'));
      document.body.classList.add('is-locked');
      const first = $('a, button', drawer); if (first) first.focus({ preventScroll: true });
    };
    openers.forEach((b) => b.addEventListener('click', open));
    $$('[data-menu-close]', drawer).forEach((b) => b.addEventListener('click', close));
    $$('a', drawer).forEach((a) => a.addEventListener('click', close));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && drawer.classList.contains('is-open')) close(); });
  }

  /* ---------- Reveal on scroll ---------- */
  function initReveal(root = document) {
    const items = $$('[data-reveal]:not(.is-in)', root);
    if (!items.length) return;
    if (!('IntersectionObserver' in window) || reduceMotion) { items.forEach((el) => el.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) { entry.target.classList.add('is-in'); io.unobserve(entry.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach((el) => io.observe(el));
  }

  /* ---------- Hero parallax (light) ---------- */
  function initParallax() {
    if (reduceMotion) return;
    const els = $$('[data-parallax]');
    if (!els.length) return;
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      els.forEach((el) => { if (y < window.innerHeight * 1.2) el.style.transform = `translate3d(0, ${y * 0.18}px, 0)`; });
      ticking = false;
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
  }

  /* ---------- Cart ---------- */
  const Cart = {
    get el() { return $('[data-cart-drawer]'); },
    open() {
      const el = this.el; if (!el) return;
      this.scrollY = window.scrollY;
      el.classList.add('is-open'); el.setAttribute('aria-hidden', 'false');
      document.body.classList.add('is-locked');
      const btn = $('[data-cart-close]', el); if (btn) btn.focus({ preventScroll: true });
    },
    close() {
      const el = this.el; if (!el) return;
      el.classList.remove('is-open'); el.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('is-locked');
      if (document.activeElement && el.contains(document.activeElement)) document.activeElement.blur();
      if (typeof this.scrollY === 'number' && Math.abs(window.scrollY - this.scrollY) > 2) window.scrollTo({ top: this.scrollY, behavior: 'instant' });
    },
    sectionId() { return 'cart-drawer'; },
    render(sections) {
      const id = this.sectionId();
      if (!sections || !id || !sections[id]) return;
      const doc = new DOMParser().parseFromString(sections[id], 'text/html');
      const fresh = $('[data-cart-inner]', doc);
      const current = $('[data-cart-inner]', this.el);
      if (fresh && current) current.replaceWith(fresh);
      const count = fresh ? fresh.dataset.count : null;
      if (count !== null) $$('[data-cart-count]').forEach((c) => { c.dataset.count = count; c.textContent = count === '0' ? '' : count; });
    },
    async add(formData) {
      const id = this.sectionId();
      if (id) formData.append('sections', id);
      formData.append('sections_url', window.location.pathname);
      const res = await fetch(`${A.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-Alandra': '1' }, body: formData });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Erreur');
      this.render(data.sections);
      return data;
    },
    async change(line, quantity) {
      const drawer = $('.drawer', this.el);
      if (drawer) drawer.classList.add('is-busy');
      try {
        const res = await fetch(`${A.routes.cartChange}.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: [this.sectionId()], sections_url: window.location.pathname })
        });
        const data = await res.json();
        this.render(data.sections);
      } finally {
        const d = $('.drawer', this.el); if (d) d.classList.remove('is-busy');
      }
    }
  };
  A.Cart = Cart;

  /* Refresh + open the drawer when an app (e.g. a bundle app) adds to cart itself */
  (function watchAppAdds() {
    if (!window.fetch) return;
    const nativeFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const res = await nativeFetch(...args);
      try {
        const [input, init] = args;
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const headers = (init && init.headers) || {};
        const ours = headers instanceof Headers ? headers.get('X-Alandra') : headers['X-Alandra'];
        if (!ours && /\/cart\/add(\.js)?(\?|$)/.test(url) && res.ok && Cart.el) {
          nativeFetch(`${A.routes.root || '/'}?sections=${Cart.sectionId()}`)
            .then((r) => r.json())
            .then((sections) => { Cart.render(sections); Cart.open(); })
            .catch(() => {});
        }
      } catch (e) { /* ignore */ }
      return res;
    };
  })();

  function initCart() {
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-cart-open]');
      if (opener && Cart.el) { e.preventDefault(); Cart.open(); return; }
      if (e.target.closest('[data-cart-close]')) { e.preventDefault(); Cart.close(); return; }
      const remove = e.target.closest('[data-line-remove]');
      if (remove && remove.closest('[data-cart-drawer]')) { e.preventDefault(); Cart.change(Number(remove.dataset.lineRemove), 0); return; }
      const step = e.target.closest('[data-line-step]');
      if (step && step.closest('[data-cart-drawer]')) {
        e.preventDefault();
        const line = Number(step.dataset.line);
        const next = Math.max(0, Number(step.dataset.qty) + Number(step.dataset.lineStep));
        Cart.change(line, next);
      }
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && Cart.el && Cart.el.classList.contains('is-open')) Cart.close(); });
  }

  /* ---------- Quantity steppers (product) ---------- */
  function initQty(root = document) {
    $$('[data-qty]', root).forEach((wrap) => {
      const input = $('input', wrap);
      $$('[data-step]', wrap).forEach((b) => b.addEventListener('click', () => {
        const v = Math.max(1, (parseInt(input.value, 10) || 1) + Number(b.dataset.step));
        input.value = v; input.dispatchEvent(new Event('change', { bubbles: true }));
      }));
    });
  }

  /* ---------- Product ---------- */
  function initProduct(root) {
    const dataEl = $('[data-product-json]', root);
    if (!dataEl) return;
    const product = JSON.parse(dataEl.textContent);
    const form = $('[data-product-form]', root);
    const idInput = $('input[name="id"]', form);
    const priceEl = $('[data-price]', root);
    const compareEl = $('[data-compare]', root);
    const saveEl = $('[data-save]', root);
    const atc = $('[data-atc]', root);
    const atcLabel = $('[data-atc-label]', root);
    const sticky = $('[data-sticky-atc]', root) || (root.dataset.primary === 'true' ? $('[data-sticky-atc]') : null);
    const qtyInput = $('input[name="quantity"]', form);
    const gallery = $('[data-gallery]', root);
    const errorEl = $('[data-form-error]', root);
    const saveFormat = root.dataset.saveLabel || 'Économisez [amount]';

    const selected = () => $$('[data-option]', root).map((fs) => { const c = $('input:checked', fs); return c ? c.value : null; });

    const currentVariant = () => {
      const opts = selected();
      return product.variants.find((v) => v.options.every((o, i) => o === opts[i])) || null;
    };

    const markAvailability = () => {
      const opts = selected();
      $$('[data-option]', root).forEach((fs, idx) => {
        $$('input', fs).forEach((input) => {
          const test = opts.slice(); test[idx] = input.value;
          const match = product.variants.find((v) => v.options.every((o, i) => o === test[i]));
          input.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    };

    const render = (variant) => {
      $$('[data-option]', root).forEach((fs, i) => { const out = $('[data-option-value]', fs); if (out) out.textContent = selected()[i] || ''; });
      if (!variant) {
        atc.disabled = true; if (atcLabel) atcLabel.textContent = 'Indisponible';
        return;
      }
      idInput.value = variant.id;
      const price = formatMoney(variant.price);
      if (priceEl) priceEl.textContent = price;
      if (compareEl) compareEl.textContent = variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : '';
      if (saveEl) saveEl.textContent = variant.compare_at_price > variant.price ? saveFormat.replace('[amount]', formatMoney(variant.compare_at_price - variant.price)) : '';
      atc.disabled = !variant.available;
      if (atcLabel) atcLabel.textContent = variant.available ? (atc.dataset.showPrice === 'true' ? `${atc.dataset.label} · ${price}` : atc.dataset.label) : 'Épuisé';
      if (sticky) {
        const sp = $('[data-sticky-price]', sticky); if (sp) sp.textContent = price;
        const sb = $('[data-sticky-btn]', sticky); if (sb) sb.disabled = !variant.available;
      }
      if (variant.featured_media && gallery) gallery.dispatchEvent(new CustomEvent('gallery:goto', { detail: { mediaId: variant.featured_media.id } }));
      if (root.dataset.primary === 'true' && window.history.replaceState) {
        const url = new URL(window.location.href); url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      markAvailability();
    };

    root.addEventListener('change', (e) => {
      if (e.target.closest('[data-option]')) render(currentVariant());
      if (e.target.matches('[data-bundle] input')) { if (qtyInput) qtyInput.value = e.target.value; }
    });
    markAvailability();

    form.addEventListener('submit', async (e) => {
      if (!Cart.el) return;
      e.preventDefault();
      if (errorEl) errorEl.textContent = '';
      atc.classList.add('is-loading'); atc.setAttribute('aria-busy', 'true');
      try {
        await Cart.add(new FormData(form));
        Cart.open();
      } catch (err) {
        if (errorEl) errorEl.textContent = err.message;
      } finally {
        atc.classList.remove('is-loading'); atc.removeAttribute('aria-busy');
      }
    });

    if (sticky) {
      const btn = $('[data-sticky-btn]', sticky);
      if (btn) btn.addEventListener('click', () => { if (typeof form.requestSubmit === 'function') form.requestSubmit(); else atc.click(); });
      if ('IntersectionObserver' in window) {
        let atcVisible = true; let pastTop = false;
        const sync = () => sticky.classList.toggle('is-visible', !atcVisible && pastTop);
        new IntersectionObserver(([en]) => { atcVisible = en.isIntersecting; pastTop = en.boundingClientRect.top < 0; sync(); }).observe(atc);
        const footer = $('.footer');
        if (footer) new IntersectionObserver(([en]) => { if (en.isIntersecting) sticky.classList.remove('is-visible'); else sync(); }).observe(footer);
      }
    }
  }

  /* ---------- Gallery ---------- */
  function initGallery(gallery) {
    const track = $('[data-gallery-track]', gallery);
    if (!track) return;
    const slides = $$('[data-media-id]', track);
    const dots = $$('[data-gallery-dot]', gallery);
    const thumbs = $$('[data-gallery-thumb]', gallery);
    let index = 0;
    const setActive = (i) => {
      index = i;
      dots.forEach((d, n) => d.setAttribute('aria-current', n === i ? 'true' : 'false'));
      thumbs.forEach((t, n) => t.setAttribute('aria-current', n === i ? 'true' : 'false'));
    };
    const goTo = (i, smooth = true) => {
      i = (i + slides.length) % slides.length;
      track.scrollTo({ left: slides[i].offsetLeft, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
      setActive(i);
    };
    let t;
    track.addEventListener('scroll', () => { clearTimeout(t); t = setTimeout(() => setActive(Math.round(track.scrollLeft / track.clientWidth)), 60); }, { passive: true });
    dots.forEach((d, n) => d.addEventListener('click', () => goTo(n)));
    thumbs.forEach((b, n) => b.addEventListener('click', () => goTo(n)));
    const prev = $('[data-gallery-prev]', gallery); const next = $('[data-gallery-next]', gallery);
    if (prev) prev.addEventListener('click', () => goTo(index - 1));
    if (next) next.addEventListener('click', () => goTo(index + 1));
    gallery.addEventListener('gallery:goto', (e) => {
      const i = slides.findIndex((s) => s.dataset.mediaId === String(e.detail.mediaId));
      if (i > -1) goTo(i);
    });

    // Desktop hover zoom
    if (window.matchMedia('(hover: hover) and (min-width: 990px)').matches) {
      slides.forEach((slide) => {
        const img = $('img', slide); if (!img) return;
        slide.addEventListener('click', () => slide.classList.toggle('is-zoom'));
        slide.addEventListener('mousemove', (e) => {
          const r = slide.getBoundingClientRect();
          slide.style.setProperty('--zx', `${((e.clientX - r.left) / r.width) * 100}%`);
          slide.style.setProperty('--zy', `${((e.clientY - r.top) / r.height) * 100}%`);
        });
        slide.addEventListener('mouseleave', () => slide.classList.remove('is-zoom'));
      });
    }
  }

  /* ---------- Delivery estimate ---------- */
  function initDelivery(root = document) {
    $$('[data-delivery]', root).forEach((el) => {
      const min = Number(el.dataset.min || 0); const max = Number(el.dataset.max || 0);
      if (!min || !max) return;
      const addBusinessDays = (date, days) => {
        const d = new Date(date);
        while (days > 0) { d.setDate(d.getDate() + 1); const wd = d.getDay(); if (wd !== 0 && wd !== 6) days -= 1; }
        return d;
      };
      const fmt = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short' });
      const now = new Date();
      el.textContent = `entre le ${fmt.format(addBusinessDays(now, min))} et le ${fmt.format(addBusinessDays(now, max))}`;
    });
  }

  /* ---------- Before / After ---------- */
  function initCompare(root = document) {
    $$('[data-compare]', root).forEach((wrap) => {
      const range = $('input[type="range"]', wrap);
      if (!range) return;
      const set = () => wrap.style.setProperty('--pos', `${range.value}%`);
      range.addEventListener('input', set); set();
    });
  }

  /* ---------- Video ---------- */
  function initVideo(root = document) {
    $$('[data-video]', root).forEach((frame) => {
      const btn = $('[data-video-play]', frame);
      const video = $('video', frame);
      const sound = $('[data-video-sound]', frame);
      if (video) {
        if (btn) btn.addEventListener('click', () => { video.muted = false; video.controls = true; video.play(); frame.classList.add('is-playing'); if (sound) sound.hidden = true; });
        if (sound) sound.addEventListener('click', () => { video.muted = !video.muted; sound.setAttribute('aria-pressed', String(!video.muted)); });
        if ('IntersectionObserver' in window && video.autoplay) {
          new IntersectionObserver(([en]) => { if (en.isIntersecting) video.play().catch(() => {}); else video.pause(); }, { threshold: 0.25 }).observe(video);
        }
      } else if (btn && frame.dataset.youtube) {
        btn.addEventListener('click', () => {
          const iframe = document.createElement('iframe');
          iframe.src = `https://www.youtube-nocookie.com/embed/${frame.dataset.youtube}?autoplay=1&playsinline=1&rel=0`;
          iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
          iframe.allowFullscreen = true; iframe.title = frame.dataset.title || 'Vidéo';
          frame.appendChild(iframe); frame.classList.add('is-playing');
        });
      }
    });
  }

  /* ---------- Init ---------- */
  function initAll(root = document) {
    initReveal(root);
    initQty(root);
    initDelivery(root);
    initCompare(root);
    initVideo(root);
    $$('[data-gallery]', root).forEach(initGallery);
    $$('[data-product]', root).forEach(initProduct);
  }

  document.addEventListener('DOMContentLoaded', () => {
    initHeader(); initMenu(); initParallax(); initCart(); initAll();
  });

  // Theme editor support
  document.addEventListener('shopify:section:load', (e) => { initAll(e.target); initHeader(); initMenu(); });
})();
